import React, { useEffect, useState } from "react";
import {
  Button,
  FormLabel,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import { textStyle } from "../../styles/Style";
import "../../../components/styles/popupStyle.css";
import {
  checkData,
  checkDuplicate,
  deleteItem,
  updateData,
} from "../../actions/actionItems";
import DraggableItemList from "../../subcomponents/DNDComponent";
import { buttonValues, DNDFieldsEnum } from "../../../constants/Types";
import { SpaPropertyEnum } from "../../../constants/NavbarList";
import { ClearButton } from "../../subcomponents/ClearButton";
import { CopyButton } from "../../subcomponents/CopyButton";
import {
  validateString,
  validationLength,
  validationMessages,
  validationType,
} from "../../actions/validations";
import { spaTooltipHeaders } from "../../../constants/TooltipInformation";
import { tooltipComponent } from "../../subcomponents/tooltipComponent";
import AlertComponent from "../../subcomponents/AlertComponent";

const SpaPriceType = ({
  data,
  fieldValues,
}: {
  data: (value: any) => void;
  fieldValues: any;
}) => {
  const [priceTypeCode, setPriceTypeCode] = useState<string | null>(null);
  const [priceTypeDescription, setPriceTypeDescription] = useState<
    string | null
  >(null);
  const [codeFieldError, setCodeFieldError] = useState<boolean>(false);
  const [codeFieldErrorMessage, setCodeFieldErrorMessage] =
    useState<string>("");
  const [descriptionFieldError, setDescriptionFieldError] =
    useState<boolean>(false);
  const [descriptionFieldErrorMessage, setDescriptionFieldErrorMessage] =
    useState<string>("");

  const [editClicked, setEditClicked] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [items, setItems] = useState<any[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [lastUpdatedRecord, setLastUpdatedRecord] = useState<any>(null);

  const validatePriceTypeFields = (): boolean => {
    if (!priceTypeCode || priceTypeCode.length === 0) {
      setAlertMessage("Price Type Code is mandatory");
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }
    setCodeFieldError(false);
    setCodeFieldErrorMessage("");
    if (
      !validateString(
        priceTypeCode,
        validationLength.spaPriceTypeCode,
        validationType.CODE,
      )
    ) {
      setCodeFieldError(true);
      setCodeFieldErrorMessage(validationMessages.spaPriceTypeCode);
      return false;
    }

    if (!priceTypeDescription || priceTypeDescription.length === 0) {
      setAlertMessage("Price Type Description is mandatory");
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }
    setDescriptionFieldError(false);
    setDescriptionFieldErrorMessage("");
    if (
      !validateString(
        priceTypeDescription,
        validationLength.spaPriceTypeDescription,
        validationType.SPACE,
      )
    ) {
      setDescriptionFieldError(true);
      setDescriptionFieldErrorMessage(
        validationMessages.spaPriceTypeDescription,
      );
      return false;
    }

    const listForDuplicate = selectedId
      ? items.filter((i: any) => String(i.id) !== String(selectedId))
      : items;
    if (
      checkDuplicate(
        listForDuplicate,
        DNDFieldsEnum.SPAPRICETYPECODE,
        priceTypeCode,
      )
    ) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }

    return true;
  };

  useEffect(() => {
    fieldValues && setItems(fieldValues);
  }, []);

  useEffect(() => {
    data(items);
    setSelectedId(checkData(items, selectedId) ? selectedId : null);
  }, [items]);

  const updateItem = () => {
    if (!validatePriceTypeFields()) {
      return false;
    }
    setItems((prev) =>
      prev.map((item: any) =>
        item.id === selectedId ? { ...item, ...payload } : item,
      ),
    );
    editItem(null);
    setEditClicked(false);
    setSelectedId(null);
    setAlertMessage(`${SpaPropertyEnum.priceTypes} updated successfully`);
    setAlertType("success");
    setOpenAlert(true);
  };

  const addItem = () => {
    if (!validatePriceTypeFields()) {
      return false;
    }
    setItems((prev) =>
      updateData(
        prev?.length
          ? [...prev, { id: prev.length + 1, ...payload }]
          : [{ id: 1, ...payload }],
      ),
    );
    setLastUpdatedRecord(payload);
    editItem(null);
    setEditClicked(false);
  };

  const editItem = (list: any) => {
    list?.id && setSelectedId(list.id);
    if (list != null) {
      setEditClicked(true);
      setPriceTypeCode(list["Price Type Code"] ?? null);
      setPriceTypeDescription(list["Price Type Description"] ?? null);
    } else {
      setPriceTypeCode(null);
      setPriceTypeDescription(null);
      setCodeFieldError(false);
      setCodeFieldErrorMessage("");
      setDescriptionFieldError(false);
      setDescriptionFieldErrorMessage("");
    }
  };

  const deleteItemById = (id: number) => {
    setItems(deleteItem(items, id));
  };

  const payload = {
    "Price Type Code": priceTypeCode,
    "Price Type Description": priceTypeDescription,
  };

  return (
    <Stack direction="column" gap="10px">
      <Paper
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          padding: "20px",
        }}
      >
        <Stack
          direction="row"
          width="100%"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h6" sx={textStyle}>
            Price Types
            <Tooltip
              title={tooltipComponent(
                spaTooltipHeaders?.priceTypeHeader ?? "Price Types",
              )}
            >
              <InfoIcon fontSize="small" />
            </Tooltip>
          </Typography>
          <Stack direction="row" gap="10px">
            {editClicked && (
              <ClearButton
                click={() => {
                  editItem(null);
                  setSelectedId(null);
                }}
              />
            )}
            {lastUpdatedRecord && (
              <CopyButton click={() => editItem(lastUpdatedRecord)} />
            )}
          </Stack>
        </Stack>
        <Stack direction="column" width="100%" gap="20px" flexWrap="wrap">
          <Stack direction="column">
            <Stack direction="row">
              <FormLabel>Price Type Code</FormLabel>
              <Tooltip
                title={tooltipComponent(
                  spaTooltipHeaders.priceTypePriceTypeCode,
                )}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Stack>{" "}
            <TextField
              variant="outlined"
              placeholder="Price Type Code"
              value={priceTypeCode ?? ""}
              error={codeFieldError}
              helperText={codeFieldError ? codeFieldErrorMessage : ""}
              onChange={(e) => setPriceTypeCode(e.target.value || null)}
              fullWidth
            />
          </Stack>
          <Stack direction="column">
            <Stack direction="row">
              <FormLabel>Price Type Description</FormLabel>
              <Tooltip
                title={tooltipComponent(
                  spaTooltipHeaders.priceTypePriceTypeDescription,
                )}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Stack>{" "}
            <TextField
              variant="outlined"
              placeholder="Price Type Description"
              value={priceTypeDescription ?? ""}
              onChange={(e) => setPriceTypeDescription(e.target.value || null)}
              fullWidth
              helperText={
                descriptionFieldError ? descriptionFieldErrorMessage : ""
              }
              error={descriptionFieldError}
            />
          </Stack>
        </Stack>
        <Stack direction="row" justifyContent="flex-end">
          <Button
            variant="contained"
            sx={{
              cursor: "pointer",
              border: "1px solid gray",
              borderRadius: "10px",
              background: "darkgreen",
              color: "white",
            }}
            onClick={() => (selectedId ? updateItem() : addItem())}
          >
            {selectedId ? buttonValues.UPDATE : buttonValues.ADD} Price Type
          </Button>
        </Stack>
        <Stack direction="column" gap="10px" mt={4}>
          <Typography variant="body2" sx={{ color: "red", fontWeight: 800 }}>
            Do not use sample data. Those are for demo purposes only!
          </Typography>
          <Typography variant="h6">Price Types Available</Typography>
          {items ? (
            <DraggableItemList
              itemData={items}
              onOrderUpdate={(order) => setItems(order)}
              onEdit={editItem}
              onDelete={deleteItemById}
              code={DNDFieldsEnum.SPAPRICETYPECODE}
              name={DNDFieldsEnum.SPAPRICETYPEDESCRIPTION}
            />
          ) : (
            <Typography variant="body2">No Price Types Available</Typography>
          )}
        </Stack>
      </Paper>
      <AlertComponent
        message={alertMessage}
        type={alertType}
        alertOpen={openAlert}
        closeAlert={() => setOpenAlert(false)}
      />
    </Stack>
  );
};

export default SpaPriceType;
